/**
 * WHAT MISSION CONTROL KNOWS, THE MOMENT IT KNOWS IT — the studio's
 * `src/components/runProgress.ts` (chapter "Mission control's first minutes",
 * packages C, D and E), ported to this app's node kinds.
 *
 * Every derivation here is pure and reads only the PROJECTED node rows the poll
 * already ships (`useRunPoll`) plus what the screen already has. No new channel, no
 * route change — which is why the whole surface is testable by replaying rows.
 *
 * ## The one rule these derivations exist to keep
 *
 * > Show the most specific true thing you know — and never invent the rest.
 *
 * A measure with no denominator yet gets NO number: it says "working", never 0 %
 * (`runKpis`, `progressLine`). What it may NOT do is withhold a number it has.
 *
 * ## Instances, not the batched aggregate (C1)
 *
 * A fan-out's key-`''` aggregate row is written ONCE, after its last instance
 * finishes (`engine_orion.write_aggregate`), while each instance row lands the
 * moment its own call returns. Reading the aggregate is what makes a whole fan of
 * verdicts appear as one jump after a frozen table. So the board is built from the
 * INSTANCE rows — never merged with the aggregate: `commit_edit` writes the instance
 * row and then rewrites the aggregate, so there is no state in which the aggregate is
 * fresher. The aggregate IS the right read for a singleton (`extract`,
 * `merge_frameworks`, `auto_verify`), whose key-`''` row is its own output.
 *
 * ## WHERE THE PORT DIVERGES FROM THE STUDIO, and why
 *
 *  * **The kinds are this pipeline's** (`graph.py`): `extract` not
 *    `extract_statements`, `aggregate_evidence` not `aggregate_article_evidence`,
 *    `validity`/`mentions`/`fill`/`coverage` not their studio spellings, and
 *    `declaration_result` has no studio counterpart at all.
 *  * **`reconcile` is NOT counted.** The studio counts its condition-reconciliation
 *    fan against the covered-cell set. Here `reconcile_fan_instances` skips a cell
 *    with nothing captured, so the fan is NARROWER than the covered set and counting
 *    it against that denominator would stall at "12 of 48" and read as unfinished
 *    work forever — the exact failure the studio's own rule forbids.
 *  * **A cell address is the PUBLIC `article_number`**, translated at the poll route.
 *    The board's own keys are already in that space, so `cellKey` matches on the nose.
 */
import { cellKey, type CellState } from "./cellState"

// ── The wire shapes (RunNode, restated for what this module reads) ──────────────

/** One projected node row, exactly as the poll ships it. */
export interface RunNode {
  kind: string
  /** `''` is the kind's aggregate; anything else is one fan-out instance. */
  key: string
  status: string
  output?: unknown
}

/** `extract`'s leaf: the board's columns, and the honest statement denominator. */
interface ExtractLeaf {
  framework?: string | null
  statements?: number | null
}
/** `determine_verdicts`' leaf: one board cell's colour. */
interface VerdictLeaf {
  verdict?: string | null
  tier?: number | null
}
/** `resolve_scope`'s leaf: the articles one statement's scope resolved to. */
interface ScopeLeaf {
  article_ids?: string[] | null
}
/** `merge_frameworks`' whole projection: the step-1 summary's items. */
interface MergeProjection {
  detections?: {
    framework_slug?: string | null
    framework_label?: string | null
  }[]
}

// ── The node rows, indexed once ─────────────────────────────────────────────────

/** A status the engine will not move again on its own. */
const SETTLED = new Set(["ok", "flagged", "failed"])
/** A status that means the node produced a value. */
const VALUED = new Set(["ok", "flagged"])

export interface NodeIndex {
  /** kind → its key-`''` row. Seeded `queued` for every declared kind before the
   *  walk starts (`recorder.seed`), so its presence means "this run has this kind"
   *  and its status means "this KIND is done" for a fan-out. */
  agg: Map<string, RunNode>
  /** kind → its fan-out instance rows (key !== ''), in row order. */
  instances: Map<string, RunNode[]>
  /** Kinds with any row — aggregate OR instance — currently `running`. A fan-out's
   *  key-`''` row stays `queued` for the whole fan, so "is this kind working" can
   *  only be answered over all of its rows. */
  running: Set<string>
  /** Total rows — 0 before the first poll, which is the "no matrix yet" switch. */
  rows: number
}

export function indexNodes(nodes: readonly RunNode[]): NodeIndex {
  const ix: NodeIndex = {
    agg: new Map(),
    instances: new Map(),
    running: new Set(),
    rows: nodes.length,
  }
  for (const n of nodes) {
    if (n.key === "") ix.agg.set(n.kind, n)
    else
      (
        ix.instances.get(n.kind) ?? ix.instances.set(n.kind, []).get(n.kind)!
      ).push(n)
    if (n.status === "running") ix.running.add(n.kind)
  }
  return ix
}

const instancesOf = (ix: NodeIndex, kind: string): RunNode[] =>
  ix.instances.get(kind) ?? []

/** A kind's own status (its key-`''` row), or null when this run has no such kind. */
export function kindStatus(ix: NodeIndex, kind: string): string | null {
  return ix.agg.get(kind)?.status ?? null
}

/** The kind has finished — it will not produce more instances. */
const kindDone = (ix: NodeIndex, kind: string): boolean =>
  SETTLED.has(kindStatus(ix, kind) ?? "")

/**
 * IS THE RUN STILL PRODUCING ITS OWN WORKLIST? — the one question every surface that
 * withholds an answer mid-run turns on, so it lives here rather than inline in each
 * of them. The board hides "Resolve N issues" on it and the Needs You tab refuses to
 * hand over a stop on it; if the two computed it separately they could disagree, and
 * a tab that offers work the board says is provisional is worse than either answer.
 *
 * Three halves, each load-bearing:
 *
 *  * **Before the first poll we do not know**, and an unanswered question is not a
 *    "no" — a first paint that calls a live run settled is a defect the studio's own
 *    review round found.
 *  * **The declaration's own lifecycle is part of it.** The matrix settling is not
 *    the end of the extraction: the interface is persisted, staged and marked
 *    `processed` in a commit that lands after the run's last event, so a judgement
 *    on the matrix alone would read "complete" over a flow with nothing in it.
 *  * **`adjudicate` is the graph's last leaf** — nothing runs after it — so "has it
 *    settled" is the whole matrix question, read from STATE rather than from the
 *    terminal event (the event cursor is kept across mounts on purpose, so a remount
 *    would never see a `run_completed` it already passed). It also answers the second
 *    half of the same question, because a DEFERRED review sets that node running
 *    again.
 *
 * A run with NO ROWS is not computing. A legacy or manual declaration has no graph
 * and never completes; treating "not finished" as "computing" would leave it saying
 * "still working" forever.
 */
export function runComputing(a: {
  ix: NodeIndex
  /** A poll has returned for this declaration (`useRunPoll.polled`). */
  polled: boolean
  /** The declaration's lifecycle is still `pending`/`processing`. */
  awaitingExtraction: boolean
}): boolean {
  if (!a.polled || a.awaitingExtraction) return true
  const review = kindStatus(a.ix, "adjudicate")
  return a.ix.rows > 0 && review != null && !SETTLED.has(review)
}

/** How many of a fan-out's instances have produced a value. NEVER a denominator for
 *  its OWN phase: it counts rows that exist, and rows appear as instances START. */
const instancesDone = (ix: NodeIndex, kind: string): number =>
  instancesOf(ix, kind).filter((n) => VALUED.has(n.status)).length

/**
 * THE COVERED-CELL COUNT — the exact unit set of the one cell-keyed phase below the
 * evidence node, and the only denominator it may honestly be counted against.
 *
 * `determine_verdicts` fans over the assembled cells, not over the board: the board
 * shows articles × every column, while only the cells a statement covers carry a
 * verdict. Counting the phase against the board would stall short of its own
 * denominator and read as unfinished work forever.
 *
 * It is EXACT rather than growing because it is only answered once `aggregate_evidence`
 * has finished, which is a precondition of the phase starting (it is its dep). Null
 * before that — and a phase with no exact unit set gets NO number.
 */
export function coveredCells(ix: NodeIndex): number | null {
  if (!VALUED.has(kindStatus(ix, "aggregate_evidence") ?? "")) return null
  const n = instancesOf(ix, "aggregate_evidence").filter(
    (r) => r.status !== "failed",
  ).length
  return n > 0 ? n : null
}

// ── C1 · the board, from the per-instance results ───────────────────────────────

/** One board cell as the matrix reads it. */
export interface LiveCell {
  verdict: string | null
  tier: number
  covered: true
}

/**
 * The board's cells from the live INSTANCE rows: the verdict where it has landed,
 * else "covered" from the evidence assembly or the resolved scope. The keys are
 * byte-identical to `cellKey()` — both fans are keyed by board cell already and the
 * poll route translates them into the public article-number space, so no identity
 * scheme is invented here.
 *
 * A row is read for any status EXCEPT `failed`, as long as it holds a value. Both
 * halves are load-bearing. A recompute sets `running` while leaving the old output in
 * place, so reading a `running` row is what keeps a recomputing cell coloured-and-
 * pulsing instead of blinking hollow. A `failed` row also keeps its last output, but
 * `write_aggregate` filters it out — so the cell goes hollow, and presenting its stale
 * colour as the run's current answer would be worse than the blank.
 */
export function liveCells(ix: NodeIndex): Record<string, LiveCell> {
  const live: Record<string, LiveCell> = {}
  for (const n of instancesOf(ix, "determine_verdicts")) {
    const leaf = n.output as VerdictLeaf | null
    if (!leaf || n.status === "failed") continue
    live[n.key] = {
      verdict: leaf.verdict ?? null,
      tier: leaf.tier ?? -1,
      covered: true,
    }
  }
  for (const n of instancesOf(ix, "aggregate_evidence")) {
    if (n.output == null || n.status === "failed" || live[n.key]) continue
    live[n.key] = { verdict: null, tier: -1, covered: true }
  }
  for (const n of instancesOf(ix, "resolve_scope")) {
    const leaf = n.output as ScopeLeaf | null
    if (!leaf || n.status === "failed") continue
    // The key is `<framework>:<groupIndex>`; a key that is not one names no framework,
    // so it places no dot rather than half of one.
    const at = n.key.indexOf(":")
    if (at <= 0) continue
    const fw = n.key.slice(0, at)
    for (const art of leaf.article_ids ?? []) {
      const k = cellKey(fw, art)
      if (!live[k]) live[k] = { verdict: null, tier: -1, covered: true }
    }
  }
  return live
}

/** The board's COLUMNS. `extract` is a singleton, so its key-`''` row is its own
 *  output and not a batched aggregate — this read is already live. Unioned with the
 *  frameworks the screen already knows (the run's curated statements), so a manual or
 *  pre-graph declaration keeps its columns. */
export function liveColumns(ix: NodeIndex, known: readonly string[]): string[] {
  const out = ix.agg.get("extract")?.output
  const extracted = Array.isArray(out) ? (out as ExtractLeaf[]) : []
  return [
    ...new Set([
      ...extracted.map((g) => g?.framework).filter((f): f is string => !!f),
      ...known,
    ]),
  ].sort((a, b) => a.localeCompare(b))
}

// ── C2 · a live state for every cell ────────────────────────────────────────────

/**
 * The four readings a board dot can have — the ONLY classification language on the
 * board stays the three states, so a working cell is an ANIMATION over the existing
 * hollow/ring/coloured vocabulary, never a fourth hue.
 *
 * - `coloured` — its verdict landed; it has one of the three states.
 * - `working`  — its own call is in flight. It keeps whatever it already shows (a
 *   colour, or the covered ring) and pulses.
 * - `still`    — covered, but merely queued: the engine writes a fan instance's row
 *   when that instance STARTS, so an un-started cell has no row at all and its
 *   "queued" can only be inferred — covered, uncoloured, nothing in flight.
 * - `hollow`   — no statement covers it (a coverage gap).
 */
export type CellLive = "coloured" | "working" | "still" | "hollow"

export function cellLive(a: {
  state?: CellState
  covered: boolean
  inFlight: boolean
}): CellLive {
  if (a.inFlight) return "working"
  if (a.state) return "coloured"
  return a.covered ? "still" : "hollow"
}

/** The cell-keyed kinds whose in-flight instance makes a dot pulse: the verdict
 *  itself, and the evidence slice it is computed from. */
export const CELL_WORK_KINDS = [
  "determine_verdicts",
  "aggregate_evidence",
] as const

// ── C3 · one live line, in its own units ────────────────────────────────────────

/** What a phase's progress is counted in. `none` = this phase has no honest
 *  denominator, so it is named with NO count rather than an invented one. */
export type PhaseUnits = "covered-cells" | "statements" | "none"

export interface RunPhase {
  /** The line's wording, as a translation key — a present participle, because it
   *  names work in flight. */
  labelKey: string
  /** The node kinds this phase covers, in the run's own order. */
  kinds: string[]
  units: PhaseUnits
  /** The fan-out kind whose landed instances are the numerator (units ≠ none). */
  count?: string
}

/**
 * THE RUN'S PHASES, in the run's own order (`ORION_GRAPH`'s authored order, which is
 * its topological order) — the vocabulary for the one live line and the scale of the
 * meter beside it.
 *
 * A PHASE IS COUNTED IN ITS OWN UNITS, and only when that unit set is known EXACTLY:
 *
 * - `covered-cells` — the assembled cell set (`coveredCells`), the exact fan width of
 *   `determine_verdicts`, whose instances are one per assembled cell.
 * - `statements` — the statement count from `extract`, the node that decides how many
 *   there are. Only for fans that really are one-per-statement: `resolve_scope`,
 *   `verdict_meaning` and `scope_pattern` each emit an instance for every group, with
 *   no skipping (their own node docstrings say so, deliberately, against the studio).
 * - `none` — a singleton, or a fan whose width nothing knows in advance.
 *   `aggregate_evidence` is the node that DEFINES the covered set, so it cannot be
 *   counted against it; `conditions`/`connect`/`attribute`/`reconcile` each enumerate
 *   a subset nothing knows before they run. Named, never numbered.
 *
 * `declaration_result` is deliberately absent: it is the one declared node that is not
 * a run step, so it names no work a reviewer is waiting on, and a phase of pure
 * assembly would be a step of the meter that is over before it is drawn.
 */
export const RUN_PHASES: RunPhase[] = [
  { labelKey: "MC_DECL_MC_PHASE_SEGMENT", kinds: ["segment"], units: "none" },
  {
    labelKey: "MC_DECL_MC_PHASE_SUPPLIER",
    kinds: ["sup", "supplier"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_DETECT",
    kinds: ["detect", "map_frameworks", "merge_frameworks"],
    units: "none",
  },
  { labelKey: "MC_DECL_MC_PHASE_VALIDITY", kinds: ["validity"], units: "none" },
  { labelKey: "MC_DECL_MC_PHASE_MENTIONS", kinds: ["mentions"], units: "none" },
  {
    labelKey: "MC_DECL_MC_PHASE_EXTRACT",
    kinds: ["extract", "select_editions"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_SCOPE",
    kinds: ["resolve_scope"],
    units: "statements",
    count: "resolve_scope",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_PROOFS",
    kinds: ["bind_article_proofs"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_EVIDENCE",
    kinds: ["aggregate_evidence"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_VERDICTS",
    kinds: ["determine_verdicts"],
    units: "covered-cells",
    count: "determine_verdicts",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_CONDITIONS",
    kinds: ["conditions", "connect"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_ATTRIBUTE",
    kinds: ["attribute"],
    units: "none",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_RECONCILE",
    kinds: ["reconcile"],
    units: "none",
  },
  { labelKey: "MC_DECL_MC_PHASE_FILL", kinds: ["fill"], units: "none" },
  {
    labelKey: "MC_DECL_MC_PHASE_SUMMARIES",
    kinds: ["verdict_meaning", "scope_pattern"],
    units: "statements",
    count: "verdict_meaning",
  },
  {
    labelKey: "MC_DECL_MC_PHASE_AUTOVERIFY",
    kinds: ["auto_verify", "coverage"],
    units: "none",
  },
  // The final review keeps gating the issue list, and THIS is where it is named. When
  // the run is down to its last leaf, the honest account of why the list is not there
  // yet is that the review is still running.
  { labelKey: "MC_DECL_MC_PHASE_REVIEW", kinds: ["adjudicate"], units: "none" },
]

/**
 * THE PHASES THIS RUN HAS, in run order — the meter's whole scale.
 *
 * Every declared kind is seeded `queued` before the walk starts, so this set is known
 * in full from the first poll and cannot grow underneath the bar. That is the property
 * the meter needs: a denominator that moves would make the fill go backwards, which is
 * the one thing a progress bar may never do.
 */
function presentPhases(ix: NodeIndex): RunPhase[] {
  return RUN_PHASES.filter((p) => p.kinds.some((k) => ix.agg.has(k)))
}

/**
 * The phase the line names: the LAST phase (in run order) with anything running, else
 * the first phase this run has that has not finished.
 *
 * "Last running" rather than "first running" because the walk is a wavefront — several
 * independent phases overlap — and the furthest-along one is both the work that gates
 * the rest and monotonic, so the line never reads as going backwards. The fallback
 * covers the gap between waves. Null once every phase this run has is finished.
 */
export function currentPhase(ix: NodeIndex): RunPhase | null {
  const present = presentPhases(ix)
  for (let i = present.length - 1; i >= 0; i--) {
    if (present[i].kinds.some((k) => ix.running.has(k))) return present[i]
  }
  return (
    present.find((p) =>
      p.kinds.some((k) => ix.agg.has(k) && !kindDone(ix, k)),
    ) ?? null
  )
}

/** A phase is finished when every kind it names has settled. */
const phaseDone = (ix: NodeIndex, p: RunPhase): boolean =>
  p.kinds.every((k) => !ix.agg.has(k) || kindDone(ix, k))

export type PhaseStatus = "queued" | "running" | "done" | "failed"

/**
 * EVERY PHASE THIS RUN HAS, each with ONE status read over ALL of its rows —
 * aggregate and instances alike — in run order. The feed that waits on an
 * extraction (`ExtractionProgress`) shows this list; it must never be derived
 * from a slice of the matrix, because the endpoint returns the matrix sorted
 * by `(kind, key)`, not by activity (review, wave 2): a slice of its tail
 * is an alphabetical accident, not "recent work".
 *
 * Precedence per phase: `failed` if ANY row of its kinds failed (a fan-out with
 * one failed sibling is not done, whatever its siblings did); else `running`
 * if any kind is running; else `done` when every present kind has settled;
 * else `queued`. Never "the latest row wins" — sibling instances are
 * concurrent work, not successive updates to one task.
 */
export function phaseStatuses(
  ix: NodeIndex,
): { phase: RunPhase; status: PhaseStatus }[] {
  return presentPhases(ix).map((phase) => {
    const rows = phase.kinds.flatMap((k) => [
      ...(ix.agg.has(k) ? [ix.agg.get(k)!] : []),
      ...instancesOf(ix, k),
    ])
    let status: PhaseStatus
    if (rows.some((r) => r.status === "failed")) status = "failed"
    else if (phase.kinds.some((k) => ix.running.has(k))) status = "running"
    else if (phaseDone(ix, phase)) status = "done"
    else status = "queued"
    return { phase, status }
  })
}

export interface RunAdvance {
  /** 1-based position of the phase in flight, for the meter's tooltip. */
  step: number
  /** How many phases this run has. */
  steps: number
  /** 0…1 — the fill. What is left of the track is what is still missing. */
  fraction: number
}

/**
 * HOW FAR THE RUN IS FROM ITS LAST STEP — the meter beside the numbers.
 *
 * Whole phases finished, plus the phase in flight's OWN landed share where that share
 * can be counted honestly (the counted fans, which are also the long ones). Without
 * that inner term the bar would stand still for the whole verdict fan and then jump a
 * whole step — the batch-shaped progress this chapter exists to remove.
 *
 * It is deliberately NOT counted in node rows or in cells: a run's steps are not
 * equal-sized, so either of those is a bar that races and then crawls.
 */
export function runAdvance(ix: NodeIndex): RunAdvance | null {
  const present = presentPhases(ix)
  if (present.length === 0) return null
  const done = present.filter((p) => phaseDone(ix, p)).length
  const phase = currentPhase(ix)
  let inner = 0
  if (phase && !phaseDone(ix, phase) && phase.count) {
    const units = phaseUnits(ix, phase)
    if (units) inner = Math.min(1, instancesDone(ix, phase.count) / units)
  }
  return {
    step: phase ? present.indexOf(phase) + 1 : present.length,
    steps: present.length,
    fraction: Math.min(1, (done + inner) / present.length),
  }
}

const phaseUnits = (ix: NodeIndex, phase: RunPhase): number | null =>
  phase.units === "covered-cells"
    ? coveredCells(ix)
    : phase.units === "statements"
      ? statementCount(ix)
      : null

export interface ProgressLine {
  labelKey: string
  /** `{done, total}` — or null when this phase has no honest denominator. */
  count: { done: number; total: number } | null
}

/** The statement count — `extract`'s own answer to "how many are there", summed over
 *  the frameworks it extracted. Null until it has landed. */
export function statementCount(ix: NodeIndex): number | null {
  const out = ix.agg.get("extract")?.output
  if (!Array.isArray(out)) return null
  const n = (out as ExtractLeaf[]).reduce(
    (sum, g) => sum + (g?.statements ?? 0),
    0,
  )
  return n > 0 ? n : null
}

/**
 * The one live line — the current phase in ITS OWN units. Everything it needs is on
 * the node rows, so it takes no denominator from the caller: the board's dot count is
 * the KPI row's measure, not a phase's. Null when there is nothing in flight to name,
 * and the count is null whenever the phase's unit set is not yet known exactly.
 */
export function progressLine(ix: NodeIndex): ProgressLine | null {
  const phase = currentPhase(ix)
  if (!phase) return null
  const total = phaseUnits(ix, phase)
  if (!phase.count || !total) return { labelKey: phase.labelKey, count: null }
  return {
    labelKey: phase.labelKey,
    count: { done: instancesDone(ix, phase.count), total },
  }
}

// ── D · the KPI row ─────────────────────────────────────────────────────────────

/** Every KPI has THREE states: a value, working, or not started. "Not started" is
 *  never rendered as 0 — a node that never ran has no answer, and saying 0 would be
 *  inventing one (tolerant binding: an explicit "not provided"). */
export type KpiState = "value" | "working" | "not-started"

export interface Kpi {
  key: "extracted" | "coverage" | "confirmed"
  labelKey: string
  hintKey: string
  state: KpiState
  /** Present iff `state === "value"`. */
  value: string | null
  /** The node that produces it — the KPI reads that node's live status. */
  node: string
}

/** The board's dot keys — articles × columns, which is coverage's denominator (and,
 *  narrowed to the covered ones, Confirmed's). */
export function boardKeys(
  articles: readonly string[],
  columns: readonly string[],
): string[] {
  const keys: string[] = []
  for (const a of articles) for (const f of columns) keys.push(cellKey(f, a))
  return keys
}

const pct = (done: number, total: number): string =>
  `${Math.round((done / total) * 100)}%`

export interface KpiInput {
  ix: NodeIndex
  /** The run has graph rows, or the screen knows it had one. */
  hasGraph: boolean
  /** THE REGULATIONS STEP 1 KEPT — the unique frameworks over the run's INCLUDED
   *  statements. Not the readable set and not the detected one: a framework whose last
   *  statement a human ignored is out of the run's working set, and the count says
   *  so. */
  statementFrameworks: readonly string[]
  /** `articles × columns` — every dot on the board. */
  dots: readonly string[]
  /** The board dots A STATEMENT COVERS — the cells that can be confirmed at all. A
   *  hollow dot (a coverage gap) is nobody's to confirm, so counting it would put
   *  100 % out of reach. */
  coveredDots: readonly string[]
  /** The three-state colour per cell key. */
  stateCells: Record<string, CellState>
}

/**
 * The three numbers, and the heading of the screen.
 *
 * All three are LIVE in both directions: ignore a framework's last statement and
 * Extracted regulations drops; confirm a cell and Confirmed climbs. Confirmed is the
 * one that can go DOWN, and it is shown anyway — the studio's own feedback round
 * overruled the chapter's "a measure that can go down waits for the run to finish"
 * here, because the rule left the one number answering "how far am I" blank for the
 * whole run, which reads as "not computed" rather than as "not final". What survives
 * of the rule is that a measure with no denominator still shows no number.
 */
export function runKpis(input: KpiInput): Kpi[] {
  const { ix, hasGraph, statementFrameworks, dots, coveredDots } = input

  // The census is the flow derivation's, not a node's, because it is the CURATED set.
  // `extract` is still the node it reads the STATE of: it is what produces statements
  // at all, so "has this been answered yet" is its question.
  const extractStatus = kindStatus(ix, "extract")
  const extractedState: KpiState =
    statementFrameworks.length > 0 ||
    (extractStatus != null && SETTLED.has(extractStatus))
      ? "value"
      : extractStatus != null || hasGraph
        ? "working"
        : "not-started"

  // Coverage is a share of EVERY dot on the board — a detected-but-unreadable
  // framework's hollow column included, because "100 % means no blank dots anywhere".
  // It is stable only once the columns are known, which is what the empty-board case
  // waits for. Confirmed uses a different denominator on purpose (`coveredDots`).
  const coloured = dots.filter((k) => input.stateCells[k] != null).length
  const boardState: KpiState =
    dots.length > 0 ? "value" : hasGraph ? "working" : "not-started"

  const confirmed = coveredDots.filter(
    (k) => input.stateCells[k] === "confirmed",
  ).length
  // No covered cells → nothing to confirm and therefore NO DENOMINATOR, so no number is
  // invented (not 0 %, and not 100 % of nothing). Which of the two silent states it is
  // depends on whether the covered set can still grow: the evidence node is what
  // assembles it, so an unfinished one means "working" and a settled one (or no graph
  // at all) means there is no answer to have.
  const evidenceDone = !hasGraph || kindDone(ix, "aggregate_evidence")
  const confirmedState: KpiState =
    coveredDots.length > 0
      ? "value"
      : boardState !== "value"
        ? boardState
        : evidenceDone
          ? "not-started"
          : "working"

  return [
    {
      key: "extracted",
      labelKey: "MC_DECL_MC_KPI_EXTRACTED",
      hintKey: "MC_DECL_MC_KPI_EXTRACTED_HINT",
      node: "extract",
      state: extractedState,
      value:
        extractedState === "value" ? String(statementFrameworks.length) : null,
    },
    {
      key: "coverage",
      labelKey: "MC_DECL_MC_KPI_COVERAGE",
      hintKey: "MC_DECL_MC_KPI_COVERAGE_HINT",
      node: "determine_verdicts",
      state: boardState,
      value: boardState === "value" ? pct(coloured, dots.length) : null,
    },
    {
      key: "confirmed",
      labelKey: "MC_DECL_MC_KPI_CONFIRMED",
      hintKey: "MC_DECL_MC_KPI_CONFIRMED_HINT",
      node: "auto_verify",
      state: confirmedState,
      value:
        confirmedState === "value" ? pct(confirmed, coveredDots.length) : null,
    },
  ]
}

// ── E · recognized versus readable ──────────────────────────────────────────────

/**
 * A DETECTED REGULATION, as the gap list counts it: the catalogue slug it mapped to
 * (null = nothing in our registry) and the label THE DOCUMENT gave it. The label is
 * load-bearing — a regulation we cannot read has no name of ours to show, so the
 * document's own wording is the only name it has.
 */
export interface Detection {
  slug: string | null
  label: string
}

/**
 * The run's detections — the live `merge_frameworks` output, else the seed the screen
 * already has (a declaration with no graph rows).
 *
 * `merge_frameworks` is a singleton: it publishes its WHOLE detection set at once, so
 * the gap list it feeds is complete the moment it exists. Before that there are no
 * detections and therefore no list to open — which is why no half-filled one can be
 * reached.
 */
export function detectedRegulations(
  ix: NodeIndex,
  seed: readonly Detection[],
): readonly Detection[] {
  const merged = ix.agg.get("merge_frameworks")?.output as
    | MergeProjection
    | null
    | undefined
  if (!merged?.detections) return seed
  return merged.detections.map((d) => ({
    slug: d.framework_slug ?? null,
    label: d.framework_label ?? d.framework_slug ?? "",
  }))
}

/** WHY a detected regulation is not readable. Two answers, because two are all this
 *  read model can honestly tell apart — see `unreadableRegulations`. */
export type GapKind = "not-in-catalogue" | "not-readable"

/** One row of the gap list: the regulation, which gap it is, and the detection it
 *  addresses, so the row is not a dead end. */
export interface UnreadableRegulation {
  /** The row's uniqueness key AND its deep-link identity — a slug, or `label:<label>`
   *  for a detection that mapped to nothing. */
  key: string
  label: string
  slug: string | null
  gap: GapKind
}

/**
 * THE GAP IS THE LIST (E1) — the detected-versus-readable delta, as the rows it stands
 * for. The delta is never computed by subtraction: it is the list's own length, so the
 * number and the rows it stands for are one fact instead of two.
 *
 * TWO WORDINGS, AND ONLY TWO (E2, honestly). The studio distinguishes three gaps by
 * reading its CMS content per framework: not in the catalogue, in it but inactive, and
 * active but missing content fragments. This app's read model cannot see the last two
 * apart — there is no per-framework activation or content-version fact on the wire —
 * so it says what it can prove: a detection that mapped to NO catalogue slug is a
 * master-data gap, and a detection that mapped to one the run still produced no
 * statement for is named as unreadable without guessing why. Inventing the third
 * reading would be prescribing an action that may not exist.
 *
 * IN DETECTION ORDER, which is the step-1 summary's order: the row names the
 * regulation as the document names it, so it appears where the document put it.
 */
export function unreadableRegulations(
  dets: readonly Detection[],
  readable: readonly string[],
): UnreadableRegulation[] {
  const canRead = new Set(readable)
  const rows: UnreadableRegulation[] = []
  const seen = new Set<string>()
  for (const d of dets) {
    if (d.slug && canRead.has(d.slug)) continue
    const key = d.slug ?? `label:${d.label}`
    if (seen.has(key)) continue
    seen.add(key)
    rows.push({
      key,
      label: d.label || (d.slug ?? ""),
      slug: d.slug,
      gap: d.slug ? "not-readable" : "not-in-catalogue",
    })
  }
  return rows
}

// ── F1 · the counts the final review revises ────────────────────────────────────

/**
 * Are the run-scoped SUMMARIES safe to show? They are precisely what the final review
 * revises — the adjudicator demotes false-alarm advisories and signs cells off as its
 * last act — so while the run is still computing they are not shown at all, exactly as
 * the step sections withhold theirs.
 *
 * `computing` is the whole gate, and it already includes the review: `adjudicate` is
 * the graph's last leaf and the run is not complete until it has settled, so "the run
 * has completed" and "the review has run" are one fact on the initial pass. A DEFERRED
 * review fired later by a reviewer's own gesture is the second half, and it is the
 * `reviewing` flag — the cone the poll is watching.
 */
export function summaryGated(computing: boolean, reviewing: boolean): boolean {
  return computing || reviewing
}
